import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { FileRef } from '@/shared/api/types';
import { getAuthState } from '@/shared/auth/store';

import {
  applyMessageToList,
  applyMessageUpdateToList,
  applyMessageUpdateToPins,
  applyReadToList,
  belongsToView,
  flattenChatPages,
  mapChatListPages,
  patchMessage,
  pinnedMessagesOf,
  removeMessage,
  setPinnedInList,
  toggleReactionGroups,
  upsertMessage,
  type ChatListData,
  type MessagesData,
  type PageParam,
} from './lib/cache';
import { useChatGone, useMessengerStore } from './store';
import type {
  CallConfig,
  ChatDetails,
  ChatFile,
  ChatFolder,
  ChatListItem,
  ChatMemberRole,
  ChatPage,
  ChatPolicy,
  FoldersSummary,
  ForwardResult,
  IceServersResponse,
  Message,
  MessagePage,
  MessengerSettings,
  ReadResult,
  UnreadSummary,
} from './types';

/** Query keys (ARCHITECTURE §6.5: `[moduleId, …]`). */
export const messengerKeys = {
  all: ['messenger'] as const,
  chats: () => ['messenger', 'chats'] as const,
  /** The unpaged list (`GET /chats`): the top bar's recent chats and the forward dialog. */
  chatList: (search: string) => ['messenger', 'chats', 'list', search] as const,
  /** A view of the paged list: `all`, `unread`, `archive` or a folder's id. */
  chatPages: (view: string, search: string) => ['messenger', 'chats', 'page', view, search] as const,
  folders: () => ['messenger', 'folders'] as const,
  unread: () => ['messenger', 'unread'] as const,
  chat: (chatId: string) => ['messenger', 'chat', chatId] as const,
  messages: (chatId: string) => ['messenger', 'messages', chatId] as const,
  messageWindow: (chatId: string, around: string | null) =>
    ['messenger', 'messages', chatId, around ?? 'latest'] as const,
  files: (chatId: string) => ['messenger', 'files', chatId] as const,
  search: (chatId: string, query: string) => ['messenger', 'search', chatId, query] as const,
  callConfig: () => ['messenger', 'call-config'] as const,
  iceServers: () => ['messenger', 'ice-servers'] as const,
  settings: () => ['messenger', 'settings'] as const,
  groupChat: (groupId: string) => ['messenger', 'group-chat', groupId] as const,
};

const chatPath = (chatId: string) => `/chats/${encodeURIComponent(chatId)}`;

/** The most chats the unpaged list asks for (the server's largest answer). */
export const CHAT_LIST_LIMIT = 500;

/** Chats per page of the chat list (the next page loads when the list is scrolled near its end). */
export const CHAT_PAGE_SIZE = 30;

export const fetchChats = (search: string, signal?: AbortSignal) =>
  api.get<ChatListItem[]>('/chats', { query: { search: search || undefined, limit: CHAT_LIST_LIMIT }, signal });

export const fetchChatPage = (view: string, search: string, cursor: string | null, signal?: AbortSignal) =>
  api.get<ChatPage>('/chats/page', {
    query: { folder: view, search: search || undefined, cursor: cursor ?? undefined, limit: CHAT_PAGE_SIZE },
    signal,
  });

export const fetchChat = (chatId: string, signal?: AbortSignal) =>
  api.get<ChatDetails>(chatPath(chatId), { signal });

export const fetchMessages = (chatId: string, param: PageParam, signal?: AbortSignal) =>
  api.get<MessagePage>(`${chatPath(chatId)}/messages`, { query: { ...param, limit: 40 }, signal });

export const openDirectChat = (userId: string) => api.post<ChatDetails>('/chats/direct', { userId });

export const markChatRead = (chatId: string, seq: number) =>
  api.post<ReadResult>(`${chatPath(chatId)}/read`, { seq });

export const sendTyping = (chatId: string) => api.post(`${chatPath(chatId)}/typing`);

const currentUserId = () => getAuthState().user?.id ?? '';

/** The chats (pinned first, then the most recent activity) in one answer; `search` filters by title or person. */
export function useChats(search = '') {
  return useQuery({
    queryKey: messengerKeys.chatList(search),
    queryFn: ({ signal }) => fetchChats(search, signal),
    placeholderData: search ? keepPreviousData : undefined,
    staleTime: 15_000,
  });
}

/**
 * The chat list of a view (`all`, `unread`, `archive` or a folder's id) in pages: the next one is asked for when the
 * list is scrolled near its end. A search narrows the view; typing keeps the rows until the answer, another view does not.
 */
export function useChatListPages(view: string, search: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: messengerKeys.chatPages(view, search),
    queryFn: ({ pageParam, signal }) => fetchChatPage(view, search, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[3] === view ? previous : undefined),
    staleTime: 15_000,
    enabled,
  });
}

/** The person's folders with the counters of every view (tab badges). */
export function useFolders(enabled = true) {
  return useQuery({
    queryKey: messengerKeys.folders(),
    queryFn: ({ signal }) => api.get<FoldersSummary>('/chats/folders', { signal }),
    staleTime: 30_000,
    enabled,
  });
}

/** Unread totals for the navigation counters (muted chats excluded). */
export function useUnread(enabled = true) {
  return useQuery({
    queryKey: messengerKeys.unread(),
    queryFn: ({ signal }) => api.get<UnreadSummary>('/chats/unread', { signal }),
    staleTime: 30_000,
    enabled,
  });
}

export function useChat(chatId: string | undefined) {
  const gone = useChatGone(chatId);
  return useQuery({
    queryKey: messengerKeys.chat(chatId ?? ''),
    queryFn: ({ signal }) => fetchChat(chatId ?? '', signal),
    enabled: !!chatId && !gone,
    staleTime: 15_000,
  });
}

/**
 * The history of a chat as a bidirectional infinite query: pages oldest → newest; `fetchPreviousPage` loads older
 * messages, `fetchNextPage` newer ones (only for a window opened around a message).
 */
export function useMessages(chatId: string, around: string | null) {
  const gone = useChatGone(chatId);
  return useInfiniteQuery({
    queryKey: messengerKeys.messageWindow(chatId, around),
    queryFn: ({ pageParam, signal }) => fetchMessages(chatId, pageParam, signal),
    initialPageParam: around ? { around } : {},
    getPreviousPageParam: (first): PageParam | undefined =>
      first.olderCursor ? { before: first.olderCursor } : undefined,
    getNextPageParam: (last): PageParam | undefined =>
      last.newerCursor ? { after: last.newerCursor } : undefined,
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    enabled: !gone,
  });
}

export function useChatFiles(chatId: string, enabled: boolean) {
  const gone = useChatGone(chatId);
  return useQuery({
    queryKey: messengerKeys.files(chatId),
    queryFn: ({ signal }) => api.get<ChatFile[]>(`${chatPath(chatId)}/files`, { signal }),
    enabled: enabled && !gone,
  });
}

export function useMessageSearch(chatId: string, query: string) {
  return useQuery({
    queryKey: messengerKeys.search(chatId, query),
    queryFn: ({ signal }) =>
      api.get<Message[]>(`${chatPath(chatId)}/messages/search`, { query: { q: query }, signal }),
    enabled: query.trim().length >= 2,
    placeholderData: keepPreviousData,
  });
}

export function useCallConfig() {
  return useQuery({
    queryKey: messengerKeys.callConfig(),
    queryFn: ({ signal }) => api.get<CallConfig>('/calls/config', { signal }),
    staleTime: Infinity,
  });
}

/**
 * What the server would hand a browser for a call (STUN, TURN, whether the deployment forces the relay) — read by the
 * administrators' diagnostics in the messenger settings; calls themselves ask through the call manager, every time.
 */
export function useIceServers(enabled: boolean) {
  return useQuery({
    queryKey: messengerKeys.iceServers(),
    queryFn: ({ signal }) => api.get<IceServersResponse>('/calls/ice-servers', { signal }),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

// ---- chat lists in the cache ---------------------------------------------------------------------------------------
//
// A chat is in several cached lists at once: the paged views (`['messenger','chats','page',view,search]`, an infinite
// query) and the unpaged one (`['messenger','chats','list',search]`, an array: the top bar and the forward dialog). The
// helpers below patch all of them with the array helpers of `lib/cache.ts`.

type ChatListCache = ChatListData | ChatListItem[];

interface ChatListSlot {
  key: QueryKey;
  /** The view the list shows (`all` for the unpaged list). */
  view: string;
  search: string;
  data: ChatListCache;
}

function chatListSlots(queryClient: QueryClient): ChatListSlot[] {
  const slots: ChatListSlot[] = [];
  const text = (value: unknown, fallback: string) => (typeof value === 'string' ? value : fallback);
  for (const [key, data] of queryClient.getQueriesData<ChatListCache>({ queryKey: messengerKeys.chats() })) {
    if (!data) continue;
    if (key[2] === 'page') slots.push({ key, view: text(key[3], 'all'), search: text(key[4], ''), data });
    else if (key[2] === 'list') slots.push({ key, view: 'all', search: text(key[3], ''), data });
  }
  return slots;
}

const itemsOf = (data: ChatListCache): ChatListItem[] =>
  Array.isArray(data) ? data : flattenChatPages(data);

type ListUpdate = (items: ChatListItem[]) => ChatListItem[] | null | undefined;

const mapChatList = (data: ChatListCache, update: ListUpdate) =>
  Array.isArray(data) ? update(data) : mapChatListPages(data, update);

/**
 * Applies an update to every loaded chat list. Returns true when one of the lists could not derive the result
 * (`null`): the caller then refetches the lists instead of guessing.
 */
function patchChatLists(
  queryClient: QueryClient,
  update: (items: ChatListItem[], slot: ChatListSlot) => ChatListItem[] | null | undefined,
): boolean {
  let stale = false;
  for (const slot of chatListSlots(queryClient)) {
    const next = mapChatList(slot.data, (items) => update(items, slot));
    if (next === null) stale = true;
    else if (next && next !== slot.data) queryClient.setQueryData(slot.key, next);
  }
  return stale;
}

const invalidateChatLists = (queryClient: QueryClient) =>
  void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });

/** What the person arranges about a chat, as both the list item and the details carry it. */
type ChatFlags = Pick<
  ChatListItem,
  'isPinned' | 'isArchived' | 'markedUnread' | 'folderIds' | 'isMuted' | 'unreadCount' | 'unreadMentions'
>;
type ChatFlagsChange = Partial<ChatFlags> | ((current: ChatFlags) => Partial<ChatFlags>);

/**
 * Changes a chat's own flags (pinned, archived, folders, …) in every loaded list and in its details at once. Lists the
 * chat no longer belongs to (an archived chat in "all", a chat taken out of the folder on screen) drop it; pinning moves
 * it to the top.
 */
function patchChatFlags(queryClient: QueryClient, chatId: string, change: ChatFlagsChange): void {
  patchChatLists(queryClient, (items, slot) => {
    const item = items.find((c) => c.id === chatId);
    if (!item) return items;
    const patch = typeof change === 'function' ? change(item) : change;
    if (!belongsToView(slot.view, { ...item, ...patch })) return items.filter((c) => c.id !== chatId);
    const ordered = patch.isPinned === undefined ? items : setPinnedInList(items, chatId, patch.isPinned);
    return ordered.map((c) => (c.id === chatId ? { ...c, ...patch } : c));
  });
  queryClient.setQueryData<ChatDetails>(messengerKeys.chat(chatId), (chat) =>
    chat ? { ...chat, ...(typeof change === 'function' ? change(chat) : change) } : chat,
  );
}

// ---- cache updates shared by mutations and realtime ------------------------------------------------------------

/**
 * Puts a message into every loaded window of its chat and the chat lists (the chat moves to the top of the unpinned
 * chats of the lists it is in). The lists are fetched again instead when the cache cannot tell: the chat is in none of
 * them, it belongs to a view that has not loaded it, or it is archived and the server brings it back.
 */
export function cacheMessage(
  queryClient: QueryClient,
  message: Message,
  options: { readNow?: boolean } = {},
): void {
  queryClient.setQueriesData<MessagesData>({ queryKey: messengerKeys.messages(message.chatId) }, (data) =>
    upsertMessage(data, message),
  );
  const me = currentUserId();
  const slots = chatListSlots(queryClient);
  const known = slots.map((slot) => itemsOf(slot.data).find((c) => c.id === message.chatId)).find(Boolean);
  // A message of somebody else brings an archived chat back, unless it is muted (the server's rule).
  const restored = !!known && known.isArchived && !known.isMuted && message.author?.id !== me;
  if (!known || restored) {
    invalidateChatLists(queryClient);
  } else {
    const after = applyMessageToList([known], message, me, options)?.[0] ?? known;
    let stale = false;
    for (const slot of slots) {
      if (itemsOf(slot.data).some((c) => c.id === message.chatId)) {
        const next = mapChatList(slot.data, (items) => applyMessageToList(items, message, me, options));
        if (next && next !== slot.data) queryClient.setQueryData(slot.key, next);
      } else if (!slot.search && belongsToView(slot.view, after)) {
        stale = true; // the view shows this chat now, but has not loaded it
      }
    }
    if (stale) invalidateChatLists(queryClient);
  }
  queryClient.setQueryData<ChatDetails>(messengerKeys.chat(message.chatId), (chat) =>
    chat && message.seq > chat.lastSeq ? { ...chat, lastSeq: message.seq } : chat,
  );
}

/** An edited, deleted or re-reacted message. */
export function cacheMessageUpdate(queryClient: QueryClient, message: Message): void {
  queryClient.setQueriesData<MessagesData>({ queryKey: messengerKeys.messages(message.chatId) }, (data) =>
    patchMessage(data, message.id, () => message),
  );
  patchChatLists(queryClient, (items) => applyMessageUpdateToList(items, message));
  queryClient.setQueryData<ChatDetails>(messengerKeys.chat(message.chatId), (chat) =>
    chat ? applyMessageUpdateToPins(chat, message) : chat,
  );
}

/** The current user's read pointer moved (here or in another tab). */
export function cacheRead(queryClient: QueryClient, chatId: string, lastReadSeq: number): void {
  if (patchChatLists(queryClient, (items) => applyReadToList(items, chatId, lastReadSeq)))
    invalidateChatLists(queryClient);
  queryClient.setQueryData<ChatDetails>(messengerKeys.chat(chatId), (chat) => {
    if (!chat) return chat;
    const moved = lastReadSeq > chat.lastReadSeq;
    const everythingRead = lastReadSeq >= chat.lastSeq;
    // Reading clears the person's "mark as unread" even when nothing is new.
    if (!moved && !(chat.markedUnread && everythingRead)) return chat;
    return {
      ...chat,
      lastReadSeq: Math.max(chat.lastReadSeq, lastReadSeq),
      unreadCount: everythingRead ? 0 : chat.unreadCount,
      unreadMentions: everythingRead ? 0 : chat.unreadMentions,
      markedUnread: everythingRead ? false : chat.markedUnread,
      members: chat.members.map((m) => (m.user.id === currentUserId() ? { ...m, lastReadSeq } : m)),
    };
  });
  void queryClient.invalidateQueries({ queryKey: messengerKeys.unread() });
}

// ---- mutations ---------------------------------------------------------------------------------------------------

let clientSeq = 0;
const newClientId = () =>
  `local-${typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${++clientSeq}`}`;

export interface SendVariables {
  clientId: string;
  html: string;
  attachments: FileRef[];
  replyTo: Message | null;
}

/** Sends a message with an optimistic copy (replaced by the server's message, marked as failed on error). */
export function useSendMessage(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: SendVariables) =>
      api.post<Message>(`${chatPath(chatId)}/messages`, {
        html: vars.html,
        attachmentIds: vars.attachments.map((a) => a.id),
        replyToId: vars.replyTo?.id ?? null,
        clientId: vars.clientId,
      }),
    onMutate: (vars) => {
      const user = getAuthState().user;
      const optimistic: Message = {
        id: vars.clientId,
        chatId,
        seq: Number.MAX_SAFE_INTEGER,
        kind: 'text',
        author: user
          ? { id: user.id, fullName: user.fullName, avatarUrl: user.avatarUrl, position: user.position }
          : null,
        html: vars.html,
        text: '',
        systemType: null,
        systemParams: null,
        attachments: vars.attachments,
        replyTo: vars.replyTo
          ? {
              id: vars.replyTo.id,
              seq: vars.replyTo.seq,
              author: vars.replyTo.author,
              text: vars.replyTo.text,
              hasAttachments: vars.replyTo.attachments.length > 0,
              isDeleted: vars.replyTo.isDeleted,
            }
          : null,
        mentionedUserIds: [],
        reactions: [],
        createdAt: new Date().toISOString(),
        editedAt: null,
        isDeleted: false,
        clientId: vars.clientId,
        imageSizes: [],
        pending: 'sending',
      };
      queryClient.setQueriesData<MessagesData>({ queryKey: messengerKeys.messages(chatId) }, (data) =>
        upsertMessage(data, optimistic),
      );
    },
    onSuccess: (message) => {
      cacheMessage(queryClient, message, { readNow: true });
      cacheRead(queryClient, chatId, message.seq);
    },
    onError: (_error, vars) => {
      queryClient.setQueriesData<MessagesData>({ queryKey: messengerKeys.messages(chatId) }, (data) =>
        patchMessage(data, vars.clientId, (m) => ({ ...m, pending: 'failed' })),
      );
    },
    meta: { silent: true },
  });
}

export { newClientId };

/** Drops a failed optimistic message. */
export function discardMessage(queryClient: QueryClient, chatId: string, id: string): void {
  queryClient.setQueriesData<MessagesData>({ queryKey: messengerKeys.messages(chatId) }, (data) =>
    removeMessage(data, id),
  );
}

export function useEditMessage(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, html }: { messageId: string; html: string }) =>
      api.put<Message>(`${chatPath(chatId)}/messages/${messageId}`, { html }),
    onSuccess: (message) => cacheMessageUpdate(queryClient, message),
  });
}

export function useDeleteMessage(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (message: Message) => api.delete(`${chatPath(chatId)}/messages/${message.id}`),
    onMutate: (message) => {
      cacheMessageUpdate(queryClient, {
        ...message,
        isDeleted: true,
        html: '',
        text: '',
        attachments: [],
        reactions: [],
      });
      return { message };
    },
    onError: (_error, _message, context) => {
      if (context) cacheMessageUpdate(queryClient, context.message);
    },
  });
}

/** Toggles the current user's reaction (optimistic). */
export function useReact(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ message, type }: { message: Message; type: string }) => {
      const mine = message.reactions.find((g) => g.userIds.includes(currentUserId()))?.type;
      return mine === type
        ? api.delete<Message>(`${chatPath(chatId)}/messages/${message.id}/reaction`)
        : api.put<Message>(`${chatPath(chatId)}/messages/${message.id}/reaction`, { type });
    },
    onMutate: ({ message, type }) => {
      cacheMessageUpdate(queryClient, {
        ...message,
        reactions: toggleReactionGroups(message.reactions, type, currentUserId()),
      });
      return { message };
    },
    onSuccess: (message) => cacheMessageUpdate(queryClient, message),
    onError: (_error, _vars, context) => {
      if (context) cacheMessageUpdate(queryClient, context.message);
    },
  });
}

/** Pins a message (at most five; an already pinned one moves to the front). The answer is the chat with its pins. */
export function usePinMessage(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => api.put<ChatDetails>(`${chatPath(chatId)}/pin`, { messageId }),
    onSuccess: (chat) => queryClient.setQueryData(messengerKeys.chat(chatId), chat),
  });
}

/** Unpins a message (`null`: the newest pin). */
export function useUnpinMessage(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string | null) =>
      api.delete<undefined>(`${chatPath(chatId)}/pin`, { query: { messageId: messageId ?? undefined } }),
    onSuccess: (_result, messageId) =>
      queryClient.setQueryData<ChatDetails>(messengerKeys.chat(chatId), (chat) => {
        if (!chat) return chat;
        const pins = pinnedMessagesOf(chat);
        const next = messageId === null ? pins.slice(1) : pins.filter((m) => m.id !== messageId);
        return { ...chat, pinnedMessages: next, pinnedMessage: next[0] ?? null };
      }),
  });
}

export function useMuteChat(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (muted: boolean) => api.put(`${chatPath(chatId)}/mute`, { muted }),
    onMutate: (muted) => patchChatFlags(queryClient, chatId, { isMuted: muted }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: messengerKeys.unread() });
      void queryClient.invalidateQueries({ queryKey: messengerKeys.folders() });
      void queryClient.invalidateQueries({ queryKey: messengerKeys.chat(chatId) });
    },
  });
}

/**
 * What a person arranges about the chats of their own list (nobody else sees it): pin, archive, mark as unread / read,
 * clear the history, mute, put into folders. Variable-based, so a list shares one set between its rows. The cache
 * follows at once (the chat moves, the row drops out of a view it no longer belongs to); the lists are fetched again
 * when the call settles. Errors toast through the shared mutation handler.
 */
export function useChatActions() {
  const queryClient = useQueryClient();
  const refresh = (chatId: string) => {
    invalidateChatLists(queryClient);
    void queryClient.invalidateQueries({ queryKey: messengerKeys.chat(chatId) });
    void queryClient.invalidateQueries({ queryKey: messengerKeys.folders() });
    void queryClient.invalidateQueries({ queryKey: messengerKeys.unread() });
  };
  const pin = useMutation({
    mutationFn: ({ chatId, pinned }: { chatId: string; pinned: boolean }) =>
      api.put(`${chatPath(chatId)}/pinned`, { pinned }),
    onMutate: ({ chatId, pinned }) => patchChatFlags(queryClient, chatId, { isPinned: pinned }),
    onSettled: (_result, _error, { chatId }) => refresh(chatId),
  });
  const archive = useMutation({
    mutationFn: ({ chatId, archived }: { chatId: string; archived: boolean }) =>
      api.put(`${chatPath(chatId)}/archived`, { archived }),
    // Archiving unpins the chat.
    onMutate: ({ chatId, archived }) =>
      patchChatFlags(queryClient, chatId, archived ? { isArchived: true, isPinned: false } : { isArchived: false }),
    onSettled: (_result, _error, { chatId }) => refresh(chatId),
  });
  const markUnread = useMutation({
    mutationFn: ({ chatId }: { chatId: string }) => api.put(`${chatPath(chatId)}/unread`, { unread: true }),
    onMutate: ({ chatId }) => patchChatFlags(queryClient, chatId, { markedUnread: true }),
    onSettled: (_result, _error, { chatId }) => refresh(chatId),
  });
  const markRead = useMutation({
    mutationFn: ({ chatId, seq }: { chatId: string; seq: number }) => markChatRead(chatId, seq),
    onMutate: ({ chatId }) =>
      patchChatFlags(queryClient, chatId, { markedUnread: false, unreadCount: 0, unreadMentions: 0 }),
    onSuccess: (result, { chatId }) => cacheRead(queryClient, chatId, result.lastReadSeq),
    onSettled: (_result, _error, { chatId }) => refresh(chatId),
  });
  const clearHistory = useMutation({
    mutationFn: ({ chatId }: { chatId: string }) => api.delete(`${chatPath(chatId)}/history`),
    onSettled: (_result, _error, { chatId }) => {
      void queryClient.invalidateQueries({ queryKey: messengerKeys.messages(chatId) });
      void queryClient.invalidateQueries({ queryKey: messengerKeys.files(chatId) });
      refresh(chatId);
    },
  });
  const mute = useMutation({
    mutationFn: ({ chatId, muted }: { chatId: string; muted: boolean }) =>
      api.put(`${chatPath(chatId)}/mute`, { muted }),
    onMutate: ({ chatId, muted }) => patchChatFlags(queryClient, chatId, { isMuted: muted }),
    onSettled: (_result, _error, { chatId }) => refresh(chatId),
  });
  const setFolder = useMutation({
    mutationFn: ({ chatId, folderId, member }: { chatId: string; folderId: string; member: boolean }) => {
      const path = `/chats/folders/${encodeURIComponent(folderId)}/chats/${encodeURIComponent(chatId)}`;
      return member ? api.put(path) : api.delete(path);
    },
    onMutate: ({ chatId, folderId, member }) =>
      patchChatFlags(queryClient, chatId, (current) => {
        const others = (current.folderIds ?? []).filter((id) => id !== folderId);
        return { folderIds: member ? [...others, folderId] : others };
      }),
    onSettled: (_result, _error, { chatId }) => refresh(chatId),
  });
  return { pin, archive, markUnread, markRead, clearHistory, mute, setFolder };
}

export type ChatActions = ReturnType<typeof useChatActions>;

// ---- folders ----------------------------------------------------------------------------------------------------

/** Creating, renaming, deleting and ordering the person's folders. Errors are the dialog's to show (`silent`). */
export function useFolderActions() {
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: messengerKeys.folders() });
  const create = useMutation({
    mutationFn: (title: string) => api.post<ChatFolder>('/chats/folders', { title }),
    // The folder is in the cache before the caller's own `onSuccess` switches the list to it: a view of a folder the cache
    // does not know yet is taken for a deleted one and sends the person back to all chats.
    onSuccess: (folder) => {
      queryClient.setQueryData<FoldersSummary>(messengerKeys.folders(), (summary) =>
        summary && !summary.folders.some((f) => f.id === folder.id)
          ? { ...summary, folders: [...summary.folders, folder] }
          : summary,
      );
      refresh();
    },
    meta: { silent: true },
  });
  const rename = useMutation({
    mutationFn: ({ folderId, title }: { folderId: string; title: string }) =>
      api.put(`/chats/folders/${encodeURIComponent(folderId)}`, { title }),
    onSuccess: refresh,
    meta: { silent: true },
  });
  const remove = useMutation({
    mutationFn: (folderId: string) => api.delete(`/chats/folders/${encodeURIComponent(folderId)}`),
    onSettled: () => {
      refresh();
      invalidateChatLists(queryClient);
    },
  });
  const reorder = useMutation({
    mutationFn: (folderIds: string[]) => api.put('/chats/folders/order', { folderIds }),
    onMutate: (folderIds) =>
      queryClient.setQueryData<FoldersSummary>(messengerKeys.folders(), (summary) =>
        summary
          ? {
              ...summary,
              folders: folderIds.flatMap((id, position) => {
                const found = summary.folders.find((f) => f.id === id);
                return found ? [{ ...found, position }] : [];
              }),
            }
          : summary,
      ),
    onSettled: refresh,
  });
  return { create, rename, remove, reorder };
}

// ---- group chat management (owner and admins) --------------------------------------------------------------------

/** Makes a member an admin or a plain member again (the owner). The answer is the chat with the new roles. */
export function useSetMemberRole(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: Exclude<ChatMemberRole, 'owner'> }) =>
      api.put<ChatDetails>(`${chatPath(chatId)}/members/${encodeURIComponent(userId)}/role`, { role }),
    onSuccess: (chat) => queryClient.setQueryData(messengerKeys.chat(chatId), chat),
  });
}

/** Who may add members and who may write (the owner). */
export function useUpdateChatSettings(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { addMembers: ChatPolicy; write: ChatPolicy }) =>
      api.put<ChatDetails>(`${chatPath(chatId)}/settings`, input),
    onSuccess: (chat) => queryClient.setQueryData(messengerKeys.chat(chatId), chat),
  });
}

/**
 * Deletes a group chat for everybody (the owner). `onDeleted` runs before the chat's caches are dropped: it should leave
 * the chat's page, whose queries would otherwise fetch the chat again (404 now).
 */
export function useDeleteChat(chatId: string, onDeleted?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(chatPath(chatId)),
    onMutate: () => useMessengerStore.getState().setLeaving(chatId),
    onSuccess: () => {
      onDeleted?.();
      forgetChat(queryClient, chatId);
    },
    onSettled: () => useMessengerStore.getState().setLeaving(null),
  });
}

export function useCreateGroupChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; memberIds: string[]; avatarFileId: string | null }) =>
      api.post<ChatDetails>('/chats', input),
    onSuccess: (chat) => {
      queryClient.setQueryData(messengerKeys.chat(chat.id), chat);
      void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
    },
    meta: { silent: true },
  });
}

export function useOpenDirectChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => openDirectChat(userId),
    onSuccess: (chat) => {
      queryClient.setQueryData(messengerKeys.chat(chat.id), chat);
      void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
    },
  });
}

export function useUpdateChat(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title: string; avatarFileId: string | null }) =>
      api.put<ChatDetails>(chatPath(chatId), input),
    onSuccess: (chat) => {
      queryClient.setQueryData(messengerKeys.chat(chatId), chat);
      void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
    },
    meta: { silent: true },
  });
}

export function useAddMembers(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => api.post<ChatDetails>(`${chatPath(chatId)}/members`, { userIds }),
    onSuccess: (chat) => queryClient.setQueryData(messengerKeys.chat(chatId), chat),
  });
}

export function useRemoveMember(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`${chatPath(chatId)}/members/${userId}`),
    onSuccess: (_result, userId) =>
      queryClient.setQueryData<ChatDetails>(messengerKeys.chat(chatId), (chat) =>
        chat ? { ...chat, members: chat.members.filter((m) => m.user.id !== userId) } : chat,
      ),
  });
}

/**
 * Leaves a group chat. `onLeft` runs before the chat's caches are dropped: it should leave the chat's page, whose
 * queries would otherwise fetch the chat again (404 now).
 */
export function useLeaveChat(chatId: string, onLeft?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`${chatPath(chatId)}/leave`),
    onMutate: () => useMessengerStore.getState().setLeaving(chatId),
    onSuccess: () => {
      onLeft?.();
      forgetChat(queryClient, chatId);
    },
    onSettled: () => useMessengerStore.getState().setLeaving(null),
  });
}

export const openSavedChat = () => api.post<ChatDetails>('/chats/saved');

/** The caller's saved messages chat ("Избранное"), created on first use. */
export function useOpenSavedChat() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: openSavedChat,
    onSuccess: (chat) => {
      queryClient.setQueryData(messengerKeys.chat(chat.id), chat);
      void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
    },
  });
}

/**
 * The chat of a workgroup (members of the group only): opened — and created on first use — by asking for it; the answer
 * is also the chat's own query, so the chat view starts from it. Errors are the page's to show (404: not a member, 409:
 * the group is archived or too large).
 */
export function useGroupChat(groupId: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: messengerKeys.groupChat(groupId),
    queryFn: async ({ signal }) => {
      const chat = await api.post<ChatDetails>(`/chats/group/${encodeURIComponent(groupId)}`, undefined, {
        signal,
      });
      useMessengerStore.getState().setGone(chat.id, false);
      queryClient.setQueryData(messengerKeys.chat(chat.id), chat);
      void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
      return chat;
    },
    staleTime: 60_000,
    retry: false,
  });
}

export interface ForwardVariables {
  messageIds: string[];
  chatIds: string[];
  comment: string;
}

/** Forwards messages of a chat to other chats; the copies go into the caches at once (the realtime echo is idempotent). */
export function useForwardMessages(sourceChatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: ForwardVariables) =>
      api.post<ForwardResult>(`${chatPath(sourceChatId)}/forward`, {
        messageIds: vars.messageIds,
        chatIds: vars.chatIds,
        comment: vars.comment.trim() || null,
      }),
    onSuccess: (result) => {
      for (const target of result.targets) {
        for (const message of target.messages) cacheMessage(queryClient, message, { readNow: true });
        const newest = target.messages.at(-1);
        if (newest) cacheRead(queryClient, target.chatId, newest.seq);
      }
    },
    meta: { silent: true },
  });
}

/** "Save to favourites": a copy of the message in the caller's saved chat. */
export function useSaveMessage(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (message: Message) => api.post<Message>(`${chatPath(chatId)}/messages/${message.id}/save`),
    onSuccess: (copy) => {
      cacheMessage(queryClient, copy, { readNow: true });
      cacheRead(queryClient, copy.chatId, copy.seq);
      void queryClient.invalidateQueries({ queryKey: messengerKeys.chats() });
    },
    meta: { silent: true },
  });
}

/** The author takes a link preview away from their message (optimistic). */
export function useRemoveLinkPreview(chatId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ message, previewId }: { message: Message; previewId: string }) =>
      api.delete<Message>(`${chatPath(chatId)}/messages/${message.id}/previews/${previewId}`),
    onMutate: ({ message, previewId }) => {
      cacheMessageUpdate(queryClient, {
        ...message,
        linkPreviews: (message.linkPreviews ?? []).filter((p) => p.id !== previewId),
      });
      return { message };
    },
    onSuccess: (message) => cacheMessageUpdate(queryClient, message),
    onError: (_error, _vars, context) => {
      if (context) cacheMessageUpdate(queryClient, context.message);
    },
  });
}

export function useMessengerSettings(enabled = true) {
  return useQuery({
    queryKey: messengerKeys.settings(),
    queryFn: ({ signal }) => api.get<MessengerSettings>('/chats/settings', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

/** Switches link previews on or off for the whole portal (administrators). */
export function useUpdateMessengerSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { linkPreviewsEnabled: boolean }) =>
      api.put<MessengerSettings>('/chats/settings', input),
    onSuccess: (settings) => {
      queryClient.setQueryData(messengerKeys.settings(), settings);
      // Messages show (or hide) their previews by the switch: look again.
      void queryClient.invalidateQueries({ queryKey: ['messenger', 'messages'] });
    },
  });
}

/** Removes a chat the user no longer belongs to from every cache. */
export function forgetChat(queryClient: QueryClient, chatId: string): void {
  // First: queries of a chat still on screen must not fetch it again once removed.
  useMessengerStore.getState().setGone(chatId, true);
  patchChatLists(queryClient, (items) =>
    items.some((c) => c.id === chatId) ? items.filter((c) => c.id !== chatId) : items,
  );
  queryClient.removeQueries({ queryKey: messengerKeys.chat(chatId) });
  queryClient.removeQueries({ queryKey: messengerKeys.messages(chatId) });
  queryClient.removeQueries({ queryKey: messengerKeys.files(chatId) });
  void queryClient.invalidateQueries({ queryKey: messengerKeys.unread() });
  void queryClient.invalidateQueries({ queryKey: messengerKeys.folders() });
}
